import { Col, Form as AntForm, Input, Row, Select } from 'antd';
import { themeConfig } from '@theme/index';
import { useQuery } from '@apollo/client';
import {
  BATCH_SUBJECT_CONFIGURATION_FOR_RESULT_SHEET,
  BATCHES_FOR_RESULT_SHEET,
  batchesForResultSheetQuery,
  batchesForResultSheetQueryVariables,
  batchSubjectConfigurationForResultSheetQuery,
  batchSubjectConfigurationForResultSheetQueryVariables,
  COURSES_FOR_RESULT_SHEET,
  coursesForResultSheetQuery,
  coursesForResultSheetQueryVariables,
} from '@app-types/graphql-types';
import { useMst } from '@models/Root';
import { HSMSSelectOption } from '@components/shared';
import {
  getBatchClassesForSelect,
  getBatchesForSelect,
  getCourseSemestersForSelect,
  getCoursesForSelect,
  getSubjectsForSelectFromBatchSubjectConfiguration,
} from '@functions/dataFormatters';
import { Dispatch, SetStateAction, useState } from 'react';
import { useRouter } from 'next/router';

const { Option } = Select;
const { Item: FormItem } = AntForm;

interface ResultSheetConfigurationProps {
  selectedBatch: batchesForResultSheetQuery['batch'][0];
  selectedBatchClass: batchesForResultSheetQuery['batch'][0]['batch_classes'][0];
  selectedSubject: batchSubjectConfigurationForResultSheetQuery['batch_subject_configuration'][0];
  setSelectedBatch: Dispatch<SetStateAction<batchesForResultSheetQuery['batch'][0]>>;
  setSelectedBatchClass: Dispatch<SetStateAction<batchesForResultSheetQuery['batch'][0]['batch_classes'][0]>>;
  setSelectedSubject: Dispatch<
    SetStateAction<batchSubjectConfigurationForResultSheetQuery['batch_subject_configuration'][0]>
  >;
}

export const ResultSheetConfiguration = ({
  selectedBatch,
  setSelectedBatch,
  selectedBatchClass,
  setSelectedBatchClass,
  selectedSubject,
  setSelectedSubject,
}: ResultSheetConfigurationProps) => {
  const [selectedSemester, setSelectedSemester] =
    useState<coursesForResultSheetQuery['course'][0]['course_semesters'][0]>();
  const [selectedCourse, setSelectedCourse] = useState<coursesForResultSheetQuery['course'][0]>();
  const router = useRouter();

  const { id: resultSheetId } = router.query;
  const { user, resultSheet } = useMst();
  const { semesterId, batchClassId } = useMst().resultSheet;

  let coursesVariables: coursesForResultSheetQueryVariables;

  if (resultSheetId) {
    coursesVariables = {
      where: {
        batches: {
          batch_subject_configurations: {
            result_sheets: {
              id: {
                _eq: resultSheetId,
              },
            },
          },
        },
      },
    };
  } else {
    coursesVariables = {
      where: {
        course_semesters: {
          course_semester_subjects_subjects: {
            subject: {
              subject_to_users: {
                user_public: {
                  id: { _eq: user.id },
                },
              },
            },
          },
        },
      },
    };
  }

  const { data: coursesData, loading: courseDataLoading } = useQuery<
    coursesForResultSheetQuery,
    coursesForResultSheetQueryVariables
  >(COURSES_FOR_RESULT_SHEET, {
    skip: resultSheetId ? !semesterId : !user?.id,
    variables: coursesVariables,
    fetchPolicy: 'no-cache',
    onCompleted: (data) => {
      if (resultSheetId && data?.course[0]?.name) {
        setSelectedCourse(data.course[0]);

        setSelectedSemester(data.course[0].course_semesters.find((courseSemester) => courseSemester.id === semesterId));
      }
    },
  });

  let batchVariables: batchesForResultSheetQueryVariables;

  if (resultSheetId) {
    batchVariables = {
      where: {
        batch_classes: {
          id: {
            _eq: batchClassId,
          },
        },
      },
    };
  } else {
    batchVariables = {
      where: {
        active: {
          _eq: true,
        },
        courseId: {
          _eq: selectedCourse?.id,
        },
        batch_subject_configurations: {
          subject: {
            subject_to_users: {
              user_public: {
                id: {
                  _eq: user.id,
                },
              },
            },
          },
        },
      },
    };
  }

  const { data: batchData, loading: batchDataLoading } = useQuery<
    batchesForResultSheetQuery,
    batchesForResultSheetQueryVariables
  >(BATCHES_FOR_RESULT_SHEET, {
    skip: resultSheetId ? !batchClassId : !selectedCourse?.id,
    variables: batchVariables,
    fetchPolicy:'no-cache',
    onCompleted: (data) => {
      if (resultSheetId && data?.batch[0]) {
        setSelectedBatch(data.batch[0]);
        setSelectedBatchClass(data.batch[0].batch_classes.find((batchClass) => batchClass.id === batchClassId));
      }
    },
  });

  let batchSubjectConfigurationVariables: batchSubjectConfigurationForResultSheetQueryVariables;

  if (resultSheetId) {
    batchSubjectConfigurationVariables = {
      where: {
        result_sheets: {
          id: {
            _eq: resultSheetId,
          },
        },
      },
    };
  } else {
    batchSubjectConfigurationVariables = {
      where: {
        batchId: { _eq: selectedBatch?.id },
        subject: {
          course_semester_subjects_subjects: {
            course_semester: {
              semester: {
                _eq: selectedSemester?.semester,
              },
            },
          },
        },
      },
    };
  }

  const { data: subjectData, loading: subjectDataLoading } = useQuery<
    batchSubjectConfigurationForResultSheetQuery,
    batchSubjectConfigurationForResultSheetQueryVariables
  >(BATCH_SUBJECT_CONFIGURATION_FOR_RESULT_SHEET, {
    skip: resultSheetId ? !batchClassId : !selectedBatch || !selectedSemester,
    variables: batchSubjectConfigurationVariables,
    fetchPolicy:'no-cache',
    onCompleted: (data) => {
      if (resultSheetId && data?.batch_subject_configuration[0]) {
        setSelectedSubject(data?.batch_subject_configuration[0]);
      }
    },
  });

  const coursesForSelect: HSMSSelectOption[] = coursesData?.course ? getCoursesForSelect(coursesData.course) : [];
  const courseSemestersForSelect: HSMSSelectOption[] = selectedCourse?.course_semesters
    ? getCourseSemestersForSelect(selectedCourse.course_semesters)
    : [];
  const batchesForSelect = getBatchesForSelect(batchData?.batch);
  const batchClassesForSelect = selectedBatch?.batch_classes
    ? getBatchClassesForSelect(selectedBatch.batch_classes)
    : [];
  const subjects = subjectData?.batch_subject_configuration
    ? getSubjectsForSelectFromBatchSubjectConfiguration(subjectData.batch_subject_configuration)
    : [];

  return (
    <div id="result-sheet-configuration">
      <Row gutter={themeConfig.grid.formInputRowGroupGutter} className="timetable-configuration-main-row">
        <Col md={12} lg={8}>
          <FormItem labelCol={{ span: 24 }} label="Course">
            <Select
              onSelect={(courseId) => {
                if (selectedCourse?.id !== courseId) {
                  const selectedCourseData = coursesData.course.find((course) => course.id === courseId);

                  setSelectedCourse(selectedCourseData);
                  setSelectedSemester(undefined);
                  setSelectedBatch(undefined);
                  setSelectedBatchClass(undefined);
                  setSelectedSubject(undefined);
                }
              }}
              disabled={Boolean(resultSheetId)}
              loading={courseDataLoading}
              value={selectedCourse?.id}
              style={{ width: '100%' }}
            >
              {coursesForSelect.map((course) => (
                <Option key={course.value} value={course.value}>
                  {course.label}
                </Option>
              ))}
            </Select>
          </FormItem>
        </Col>

        <Col md={12} lg={8}>
          <FormItem labelCol={{ span: 24 }} label="Semester">
            <Select
              onSelect={(selectedSemesterId) => {
                if (selectedSemester?.id !== selectedSemesterId) {
                  const selectedSemesterData = selectedCourse.course_semesters.find(
                    (semester) => semester.semester === selectedSemesterId,
                  );

                  setSelectedSemester(selectedSemesterData);
                  setSelectedBatch(undefined);
                }
              }}
              value={selectedSemester?.semester}
              style={{ width: '100%' }}
              disabled={!selectedCourse || Boolean(resultSheetId)}
            >
              {courseSemestersForSelect.map((courseSemester) => (
                <Option key={courseSemester.value} value={courseSemester.value}>
                  {courseSemester.label}
                </Option>
              ))}
            </Select>
          </FormItem>
        </Col>

        <Col md={12} lg={8}>
          <FormItem labelCol={{ span: 24 }} label="Batch">
            <Select
              onSelect={(batchId) => {
                if (selectedBatch?.id !== batchId) {
                  const selectedBatchData = batchData.batch.find((batch) => batch.id === batchId);

                  setSelectedBatch(selectedBatchData);
                  setSelectedBatchClass(undefined);
                }
              }}
              value={selectedBatch?.id}
              style={{ width: '100%' }}
              disabled={!selectedCourse || Boolean(resultSheetId)}
              loading={batchDataLoading}
            >
              {batchesForSelect.map((batch) => (
                <Option key={batch.value} value={batch.value}>
                  {batch.label}
                </Option>
              ))}
            </Select>
          </FormItem>
        </Col>

        <Col md={12} lg={8}>
          <FormItem labelCol={{ span: 24 }} label="Class">
            <Select
              onSelect={(selectedBatchClassId) => {
                if (selectedBatchClass?.id !== selectedBatchClassId) {
                  const selectedBatchClassData = selectedBatch.batch_classes.find(
                    (batchClass) => batchClass.id === selectedBatchClassId,
                  );

                  setSelectedBatchClass(selectedBatchClassData);
                }
              }}
              value={selectedBatchClass?.id}
              style={{ width: '100%' }}
              disabled={!selectedCourse || !selectedBatch || Boolean(resultSheetId)}
              loading={batchDataLoading}
            >
              {batchClassesForSelect.map((batchClass) => (
                <Option key={batchClass.value} value={batchClass.value}>
                  {batchClass.label}
                </Option>
              ))}
            </Select>
          </FormItem>
        </Col>

        <Col md={12} lg={8}>
          <FormItem labelCol={{ span: 24 }} label="Subject">
            <Select
              onSelect={(subjectId) => {
                if (selectedSubject?.id !== subjectId) {
                  const selectedSubjectData = subjectData.batch_subject_configuration.find(
                    (subject) => subject.id === subjectId,
                  );

                  setSelectedSubject(selectedSubjectData);
                }
              }}
              value={selectedSubject?.id}
              style={{ width: '100%' }}
              disabled={!selectedCourse || !selectedBatch || Boolean(resultSheetId)}
              loading={subjectDataLoading}
            >
              {subjects.map((subject) => (
                <Option key={subject.value} value={subject.value}>
                  {subject.label}
                </Option>
              ))}
            </Select>
          </FormItem>
        </Col>

        <Col md={12} lg={8}>
          <FormItem labelCol={{ span: 24 }} label="Prepared By">
            <Input
              disabled
              value={
                resultSheet.preparedByFirstName
                  ? `${resultSheet.preparedByFirstName} ${resultSheet.preparedByLastName}`
                  : `${user.firstName} ${user.lastName}`
              }
            />
          </FormItem>
        </Col>
      </Row>
    </div>
  );
};
